import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.js';
import { cx } from './cx.js';

/**
 * Fenêtre modale : voile --color-backdrop, panneau --color-bg-surface.
 * Se ferme avec Échap, le bouton Fermer et un clic sur le voile.
 * `size="lg"` (640 px) pour les contenus larges ; 420 px par défaut.
 * `glass` : panneau en verre dépoli et voile léger — la carte reste visible
 * derrière (présentations posées sur le campus, ex. tutoriel des bureaux).
 * Rendue dans `document.body` : ouverte depuis la barre du bas (verre dépoli,
 * `backdrop-filter`), la fenêtre y était enfermée et invisible — la confirmation
 * d'« Arrêter l'enregistrement » ne s'affichait pas (Copyhouse, 2026-11-02).
 */
export function Modal({ title, open = true, onClose, footer, size = 'md', glass = false, children }: {
  title: string;
  open?: boolean;
  onClose?: () => void;
  footer?: ReactNode;
  size?: 'md' | 'lg';
  glass?: boolean;
  children?: ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // `onClose` est presque toujours une fonction fléchée recréée à chaque rendu :
  // en dépendance de l'effet, la fenêtre reprenait le focus à chaque frappe
  // et le champ en cours de saisie le perdait.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closable = !!onClose;

  // Focus à l'ouverture seulement, et pas si un champ (autoFocus) l'a déjà.
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();
  }, [open]);

  useEffect(() => {
    if (!open || !closable) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onCloseRef.current?.(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, closable]);

  if (!open) return null;
  return createPortal(
    <div
      className={cx('pl-modal-backdrop', glass && 'pl-modal-backdrop-glass')}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        ref={dialogRef}
        className={cx('pl-modal', size === 'lg' && 'pl-modal-lg', glass && 'pl-modal-glass')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className="pl-modal-head">
          <h2 className="pl-modal-title">{title}</h2>
          {onClose && (
            <button type="button" className="pl-modal-close" onClick={onClose} aria-label="Fermer">
              <Icon name="x" size={16} />
            </button>
          )}
        </div>
        <div className="pl-modal-body">{children}</div>
        {footer && <div className="pl-modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
